import isEqual from 'lodash/isEqual';

import { ErrorMap } from 'ia-react-core/~/interfaces/ErrorMap';

import { ControlFormState } from './Control';

export function checkHasValidators(errors: ErrorMap) {
  return Boolean(
    errors && Object.keys(errors).length,
  );
}

export function checkIsValid(errors: ErrorMap) {
  return Boolean(
    !errors ||
    Object.keys(errors).every((key) => (
      // Errors that will be cleared are "undefined"
      errors[key] !== true && typeof errors[key] !== 'string'
    )),
  );
}

interface ResolveInitialValueOptions<Value, StateValue> {
  value: StateValue;
  initialValue: Value | StateValue;
  defaultValue: Value | StateValue;
  invalidValue: Value | StateValue | null;
}

export function resolveInitialValue<Value, StateValue>({ value, initialValue, defaultValue, invalidValue }: ResolveInitialValueOptions<Value, StateValue>) {
  if (typeof value !== 'undefined' && value !== invalidValue) {
    return value;
  }

  if (typeof initialValue !== 'undefined' && initialValue !== invalidValue) {
    return initialValue;
  }

  return defaultValue;
}

interface ResolveValueOptions<Value, StateValue> {
  value: StateValue;
  resolvedInitialValue: Value | StateValue;
  defaultValue: Value | StateValue;
  uncommittedValue: Value | StateValue;
  preCommittedValue: Value | StateValue;
  isInitialized: boolean;
}

export function resolveValue<Value, StateValue>({ value, defaultValue, resolvedInitialValue, uncommittedValue, preCommittedValue, isInitialized }: ResolveValueOptions<Value, StateValue>) {
  if (!isInitialized && typeof value === 'undefined') {
    return resolvedInitialValue;
  }

  if (typeof uncommittedValue !== 'undefined') {
    return uncommittedValue;
  }

  if (typeof preCommittedValue !== 'undefined') {
    return preCommittedValue;
  }

  if (typeof value !== 'undefined') {
    return value;
  }

  return defaultValue;
}

export function reduceTouched(state: ControlFormState, isTouched: boolean) {
  const hasTouchedChanged = !isTouched;

  if (hasTouchedChanged) {
    return { ...state, touched: true };
  }

  return state;
}

export function reduceDirty<Value, StateValue>(state: ControlFormState, isDirty: boolean, value: StateValue, resolvedInitialValue: Value | StateValue) {
  const newIsDirty = !isEqual(value, resolvedInitialValue);
  const hasDirtyChanged = newIsDirty !== isDirty;

  if (hasDirtyChanged) {
    return { ...state, dirty: newIsDirty };
  }

  return state;
}

export function reduceErrors(state: ControlFormState, errors: ErrorMap, newErrors: ErrorMap) {
  const haveErrorsChanged = !isEqual(errors, newErrors);

  if (haveErrorsChanged) {
    return { ...state, errors: newErrors };
  }

  return state;
}

export function reduceWarnings(state: ControlFormState, warnings: ErrorMap, newWarnings: ErrorMap) {
  const haveWarningsChanged = !isEqual(warnings, newWarnings);

  if (haveWarningsChanged) {
    return { ...state, warnings: newWarnings };
  }

  return state;
}

export function reduceCautions(state: ControlFormState, cautions: ErrorMap, newCautions: ErrorMap) {
  const haveCautionsChanged = !isEqual(cautions, newCautions);

  if (haveCautionsChanged) {
    return { ...state, cautions: newCautions };
  }

  return state;
}
